/**
 * Page Contact Scolarité.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Send, MessageSquare, Phone, Mail, MapPin, ChevronRight, Loader2,
} from 'lucide-react';
import { PortailLayout } from '../PortailLayout';
import { portailApi, ContactInfo } from '../services/portailApi';

export default function Contact() {
  const navigate = useNavigate();
  const [info, setInfo] = useState<ContactInfo>({});
  const [sujet, setSujet] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    portailApi.contactInfo().then(setInfo).catch(() => {});
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim().length < 10) {
      setFeedback({ type: 'error', text: 'Message trop court (10 caractères min)' });
      return;
    }
    setSending(true);
    setFeedback(null);
    try {
      await portailApi.envoyerMessageScolarite({ sujet, message });
      setFeedback({ type: 'success', text: 'Message envoyé. La scolarité vous répondra dès que possible.' });
      setSujet('');
      setMessage('');
    } catch (e: any) {
      setFeedback({ type: 'error', text: e.message || 'Erreur d\'envoi' });
    } finally {
      setSending(false);
    }
  };

  const wa = (info.whatsapp || '').replace(/\D/g, '');
  const waLink = wa ? `https://wa.me/${wa}` : null;

  return (
    <PortailLayout showHeader title="Contact scolarité">
      <div className="space-y-3">
        {(info.whatsapp || info.email || info.tel) && (
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
            <h3 className="font-semibold text-gray-100 mb-3 text-sm">Joindre directement</h3>
            <div className="space-y-2">
              {waLink && (
                <a
                  href={waLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 bg-gray-800/50 hover:bg-gray-800 rounded-lg border border-gray-800 transition-colors"
                >
                  <MessageSquare size={20} className="text-green-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-100 text-sm">WhatsApp</div>
                    <div className="text-xs text-gray-400 truncate">{info.whatsapp}</div>
                  </div>
                  <ChevronRight size={16} className="text-gray-500 flex-shrink-0" />
                </a>
              )}
              {info.tel && (
                <a
                  href={`tel:${info.tel}`}
                  className="flex items-center gap-3 p-3 bg-gray-800/50 hover:bg-gray-800 rounded-lg border border-gray-800 transition-colors"
                >
                  <Phone size={20} className="text-blue-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-100 text-sm">Téléphone</div>
                    <div className="text-xs text-gray-400 truncate">{info.tel}</div>
                  </div>
                  <ChevronRight size={16} className="text-gray-500 flex-shrink-0" />
                </a>
              )}
              {info.email && (
                <a
                  href={`mailto:${info.email}`}
                  className="flex items-center gap-3 p-3 bg-gray-800/50 hover:bg-gray-800 rounded-lg border border-gray-800 transition-colors"
                >
                  <Mail size={20} className="text-purple-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-100 text-sm">Email</div>
                    <div className="text-xs text-gray-400 truncate">{info.email}</div>
                  </div>
                  <ChevronRight size={16} className="text-gray-500 flex-shrink-0" />
                </a>
              )}
            </div>
            {info.adresse && (
              <div className="mt-3 p-3 bg-gray-800/50 rounded text-xs text-gray-300 flex items-start gap-2">
                <MapPin size={14} className="text-gray-400 flex-shrink-0 mt-0.5" />
                {info.adresse}
              </div>
            )}
          </div>
        )}

        <form onSubmit={submit} className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          <h3 className="font-semibold text-gray-100 text-sm mb-1 flex items-center gap-2">
            <Send size={16} />
            Envoyer un message
          </h3>
          <p className="text-xs text-gray-400 mb-3">
            Votre message arrivera dans la messagerie interne de la scolarité.
          </p>
          <div className="space-y-3">
            <input
              type="text"
              value={sujet}
              onChange={(e) => setSujet(e.target.value)}
              placeholder="Sujet (optionnel)"
              className="w-full px-3 py-2.5 border border-gray-700 rounded-lg bg-gray-800 text-gray-100 placeholder-gray-500 text-sm"
            />
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              required
              minLength={10}
              placeholder="Votre message..."
              className="w-full px-3 py-2.5 border border-gray-700 rounded-lg bg-gray-800 text-gray-100 placeholder-gray-500 text-sm"
            />
            {feedback && (
              <div className={`p-2 rounded text-xs ${
                feedback.type === 'success'
                  ? 'bg-green-900/30 text-green-300 border border-green-800'
                  : 'bg-red-900/30 text-red-300 border border-red-800'
              }`}>{feedback.text}</div>
            )}
            <button
              type="submit"
              disabled={sending}
              className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              {sending ? 'Envoi...' : 'Envoyer le message'}
            </button>
          </div>
        </form>

        <button
          onClick={() => navigate('/portail/accueil')}
          className="w-full py-3 bg-gray-900 hover:bg-gray-800 border border-gray-800 text-gray-200 rounded-lg text-sm font-medium inline-flex items-center justify-center gap-2"
        >
          <ArrowLeft size={14} />
          Retour à l'accueil
        </button>
      </div>
    </PortailLayout>
  );
}
